/**
 * Generic element-insertion primitives for the page builder.
 *
 * These functions are pure: they take a section HTML string + an eid, mutate
 * via DOMParser, and return a new HTML string for store.update(). Builder-only
 * attributes (data-pb-editable, onerror) never leak into the returned HTML —
 * stripTempAttrs() runs immediately before serialization.
 *
 * Adapter modules build the new subtree's content (the social-link library,
 * the future nav-link / card / faq / button-group adapters) and call into here.
 *
 * Eid allocation rule: all newly inserted editables get fresh eids that don't
 * collide with anything already in the section. The allocator INCLUDES the
 * subtree's root element in its candidate set — if the new <a class="cs-social-link">
 * is itself classified as editable, it gets stamped too.
 *
 * newRootEid vs newPrimaryEid: newRootEid is the eid of the inserted subtree's
 * root (may be the wrapper anchor). newPrimaryEid is the eid of the first
 * non-container editable inside the subtree (typically the visible icon).
 * Selection UI should use newPrimaryEid.
 */

import { reservedEditableIds } from '../shared/editable-id-repair.js';
import { injectEditableAttrs, stripTempAttrs, escapeForAttrSelector } from './editable-utils.js';

function parseHTMLFragment(html) {
  if (typeof DOMParser === 'undefined') {
    throw new Error('DOMParser is required for element insertion');
  }
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  return { doc, root: doc.body.firstElementChild };
}

function parseSubtree(subtreeHtml) {
  if (typeof DOMParser === 'undefined') {
    throw new Error('DOMParser is required for element insertion');
  }
  const doc = new DOMParser().parseFromString(`<div>${subtreeHtml || ''}</div>`, 'text/html');
  const wrapper = doc.body.firstElementChild;
  if (!wrapper || wrapper.children.length !== 1) return null;
  return { doc, wrapper, subtree: wrapper.firstElementChild };
}

/**
 * Run injectEditableAttrs in a way that classifies the element itself, not
 * just its descendants. injectEditableAttrs walks `root.querySelectorAll(...)`
 * which excludes the root, so subtree roots like a bare <img> or <li.cs-item>
 * never get tagged unless we hand it a wrapping parent.
 */
function classifyIncludingRoot(el) {
  if (el.parentElement) {
    // Already inside a wrapper (e.g. from parseSubtree) — the wrapper itself is
    // outside our subtree, so classification on the wrapper reaches `el`.
    injectEditableAttrs(el.parentElement);
    return;
  }
  // Detached element (e.g. a cloneNode result): wrap it temporarily.
  const doc = el.ownerDocument;
  const tmp = doc.createElement('div');
  tmp.appendChild(el);
  injectEditableAttrs(tmp);
  // Detach and discard the tmp wrapper; classifications already mutated `el`.
  tmp.removeChild(el);
}

/**
 * Strip dangerous attributes (on*, javascript: hrefs) from every element in
 * the subtree, including the root. SVG content gets the heavier sanitizeSvg()
 * pass from svg-sanitizer.js at a different layer (adapter load time); this
 * is the minimum attribute-level guard for arbitrary subtree HTML.
 */
function sanitizeSubtreeAttrs(subtree) {
  const walk = (el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || '')
        .trim()
        .toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        continue;
      }
      if ((name === 'href' || name === 'xlink:href') && value.startsWith('javascript:')) {
        el.removeAttribute(attr.name);
      }
    }
  };
  walk(subtree);
  for (const el of subtree.querySelectorAll('*')) walk(el);
}

/**
 * Allocate fresh data-pb-eid values across the subtree (root included) that
 * don't collide with any existing eid on `root`. Mutates the subtree in place.
 * Returns the ordered list of elements that received an eid.
 */
function allocateEidsForSubtree(root, subtree, editableIdRepairs) {
  const used = new Set(
    Array.from(root.querySelectorAll('[data-pb-eid]')).map((el) => el.getAttribute('data-pb-eid'))
  );
  for (const eid of reservedEditableIds(editableIdRepairs)) used.add(eid);
  const matcher = '[data-pb-editable], [data-pb-eid]';
  const candidates = [subtree, ...subtree.querySelectorAll(matcher)].filter(
    (el) => el.matches && el.matches(matcher)
  );

  let counter = 0;
  for (const el of candidates) {
    let next;
    do {
      next = `e${++counter}`;
    } while (used.has(next));
    el.setAttribute('data-pb-eid', next);
    used.add(next);
  }
  return candidates;
}

// Attributes that reference an element id by value (single id, except the
// aria-* label/describe forms which are space-separated id lists).
const ID_REF_ATTRS = [
  'for',
  'aria-controls',
  'aria-labelledby',
  'aria-describedby',
  'aria-owns',
  'aria-details'
];

/**
 * Remap `id` and every intra-subtree id reference on a freshly cloned/parsed
 * subtree so a duplicated or pasted structure never collides with ids already
 * on `root` (which still contains the source when duplicating). Must be called
 * BEFORE the subtree is inserted, while it is still detached from `root`.
 *
 * Every id on the subtree is reassigned to a value not present in `root`
 * (source id stays in the used-set, so duplicates always diverge). References
 * that point at a remapped id — label[for], aria-controls/labelledby/…, and
 * fragment `href="#id"` / `xlink:href="#id"` — are rewritten to the new id;
 * references to ids OUTSIDE the subtree are left untouched (they target the page).
 */
function remapIdentityRefsForSubtree(root, subtree, opts = {}) {
  const { forceFresh = false, existingIds = [] } = opts || {};
  const usedIds = new Set(
    Array.from(root.querySelectorAll('[id]')).map((el) => el.getAttribute('id'))
  );
  for (const id of existingIds) {
    if (typeof id === 'string' && id) usedIds.add(id);
  }
  const subtreeEls = [subtree, ...subtree.querySelectorAll('*')];

  const idMap = new Map();
  for (const el of subtreeEls) {
    const oldId = el.getAttribute('id');
    if (!oldId) continue;
    let next = oldId;
    let n = 1;
    while ((forceFresh && next === oldId) || usedIds.has(next)) next = `${oldId}-${++n}`;
    el.setAttribute('id', next);
    usedIds.add(next);
    idMap.set(oldId, next);
  }
  if (idMap.size === 0) return;

  for (const el of subtreeEls) {
    for (const attr of ID_REF_ATTRS) {
      const val = el.getAttribute(attr);
      if (!val) continue;
      const remapped = val
        .split(/\s+/)
        .map((token) => (idMap.has(token) ? idMap.get(token) : token))
        .join(' ');
      if (remapped !== val) el.setAttribute(attr, remapped);
    }
    for (const hrefAttr of ['href', 'xlink:href']) {
      const href = el.getAttribute(hrefAttr);
      if (href && href.charAt(0) === '#') {
        const target = href.slice(1);
        if (idMap.has(target)) el.setAttribute(hrefAttr, `#${idMap.get(target)}`);
      }
    }
  }
}

/**
 * Pick the eid of the first non-container editable inside the subtree.
 * Must be called BEFORE stripTempAttrs (data-pb-editable disappears after).
 */
function pickPrimaryEid(subtree, fallbackRootEid) {
  if (
    subtree.getAttribute('data-pb-editable') &&
    subtree.getAttribute('data-pb-editable') !== 'container'
  ) {
    return subtree.getAttribute('data-pb-eid') || fallbackRootEid;
  }
  const candidate = subtree.querySelector('[data-pb-editable]:not([data-pb-editable="container"])');
  return candidate?.getAttribute('data-pb-eid') || fallbackRootEid;
}

function eidsInSubtree(subtree) {
  const eids = [];
  if (subtree.getAttribute('data-pb-eid')) eids.push(subtree.getAttribute('data-pb-eid'));
  for (const el of subtree.querySelectorAll('[data-pb-eid]')) {
    eids.push(el.getAttribute('data-pb-eid'));
  }
  return eids;
}

function findByEid(root, eid) {
  return root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
}

function _looksLikeHeaderFlexContainer(el) {
  if (!el || !el.children) return false;
  let candidates = 0;
  for (const child of Array.from(el.children)) {
    const tag = child.tagName?.toLowerCase();
    if (tag === 'nav' || tag === 'a' || tag === 'button' || tag === 'li') {
      candidates += 1;
      continue;
    }
    const cls = typeof child.className === 'string' ? child.className : '';
    if (/logo|button|toggle|contact|social|-group|cs-item/i.test(cls)) {
      candidates += 1;
    }
  }
  return candidates >= 2;
}

function injectHeaderZoneMarkers(root) {
  if (!root || !root.querySelectorAll) return;
  const headers = Array.from(root.querySelectorAll('header'));
  for (const header of headers) {
    if (_looksLikeHeaderFlexContainer(header) && !header.getAttribute('data-pb-reorder-zone')) {
      header.setAttribute('data-pb-reorder-zone', 'header-main');
    }

    for (const child of Array.from(header.children || [])) {
      const tag = child.tagName?.toLowerCase();
      if (
        tag === 'div' &&
        _looksLikeHeaderFlexContainer(child) &&
        !child.getAttribute('data-pb-reorder-zone')
      ) {
        child.setAttribute('data-pb-reorder-zone', 'header-main');
      }
      if (
        tag === 'ul' &&
        /top-bar|topbar/i.test(child.className || '') &&
        _looksLikeHeaderFlexContainer(child) &&
        !child.getAttribute('data-pb-reorder-zone')
      ) {
        child.setAttribute('data-pb-reorder-zone', 'header-top');
      }
    }

    const topBars = header.querySelectorAll(
      ':scope > .cs-top-bar, :scope > .cs-top, :scope > [class*="top-bar"]:not(ul)'
    );
    for (const topBar of Array.from(topBars)) {
      for (const inner of Array.from(topBar.children || [])) {
        if (
          inner.tagName?.toLowerCase() === 'div' &&
          _looksLikeHeaderFlexContainer(inner) &&
          !inner.getAttribute('data-pb-reorder-zone')
        ) {
          inner.setAttribute('data-pb-reorder-zone', 'header-top');
        }
      }
    }
  }
}

function stripHeaderZoneMarkers(root) {
  if (!root || !root.querySelectorAll) return;
  for (const el of root.querySelectorAll('[data-pb-reorder-zone]')) {
    el.removeAttribute('data-pb-reorder-zone');
  }
}

function prepareRuntimeSelectorMarkers(root, itemSelector) {
  if (typeof itemSelector === 'string' && itemSelector.includes('[data-pb-reorder-zone]')) {
    injectHeaderZoneMarkers(root);
    return true;
  }
  return false;
}

/**
 * Inject any runtime markers `itemSelector` depends on, returning whether it
 * did. `[data-pb-reorder-zone] > *` is a selector over attributes the parser
 * never writes — without this pass the header adapter's repeat roots simply do
 * not exist. Exported so callers outside the mutation primitives (16C2's
 * restyle resolver) resolve the same units the mutations do.
 */
export function withRepeatSelectorMarkers(root, itemSelector) {
  return prepareRuntimeSelectorMarkers(root, itemSelector);
}

/** Undo `withRepeatSelectorMarkers`. Safe to call unconditionally. */
export function clearRepeatSelectorMarkers(root) {
  stripHeaderZoneMarkers(root);
}

export const _test = {
  injectHeaderZoneMarkers,
  stripHeaderZoneMarkers,
  _looksLikeHeaderFlexContainer
};

const NULL_RESULT = Object.freeze({
  updatedHtml: null,
  newRootEid: null,
  newPrimaryEid: null,
  allNewEids: []
});

const NULL_MOVE_RESULT = Object.freeze({
  updatedHtml: null,
  sourceRootEid: null,
  movedEids: []
});

/**
 * Insert a sibling element near the repeat root containing `selectedEid`.
 * The primitive climbs from `selectedEid` via `closest(itemSelector)` so the
 * caller does not need to know whether the parent anchor has its own eid.
 *
 * @param {string} sectionHtml — canonical section HTML
 * @param {string} selectedEid — eid of the element the user right-clicked
 * @param {object} opts
 * @param {string} opts.itemSelector — selector that defines the repeat unit (e.g. '.cs-social-link')
 * @param {'before' | 'after'} opts.position — where to insert relative to repeat root
 * @param {string} opts.subtreeHtml — new sibling HTML (adapter-built, content-swapped)
 * @returns {{ updatedHtml: string|null, newRootEid: string|null, newPrimaryEid: string|null, allNewEids: string[] }}
 */
export function insertSiblingNearEid(sectionHtml, selectedEid, opts) {
  const { itemSelector, position, subtreeHtml } = opts || {};
  if (!sectionHtml || !selectedEid || !itemSelector || !subtreeHtml) return NULL_RESULT;
  if (position !== 'before' && position !== 'after') return NULL_RESULT;

  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return NULL_RESULT;

  injectEditableAttrs(root);

  const selected = findByEid(root, selectedEid);
  if (!selected) return NULL_RESULT;
  const repeatRoot = selected.closest(itemSelector);
  if (!repeatRoot) return NULL_RESULT;

  const parsed = parseSubtree(subtreeHtml);
  if (!parsed) return NULL_RESULT;
  const { subtree } = parsed;

  // Run editability classification on the subtree BEFORE allocating eids so
  // the new <svg>/<img>/<a>/etc. carry data-pb-editable and the allocator
  // sees them as candidates.
  classifyIncludingRoot(subtree);
  sanitizeSubtreeAttrs(subtree);
  allocateEidsForSubtree(root, subtree, opts?.editableIdRepairs);

  const newRootEid = subtree.getAttribute('data-pb-eid') || null;
  const newPrimaryEid = pickPrimaryEid(subtree, newRootEid);
  const allNewEids = eidsInSubtree(subtree);

  const imported = root.ownerDocument.importNode(subtree, true);
  if (position === 'before') {
    repeatRoot.parentNode.insertBefore(imported, repeatRoot);
  } else {
    repeatRoot.parentNode.insertBefore(imported, repeatRoot.nextSibling);
  }

  stripTempAttrs(root);
  return {
    updatedHtml: root.innerHTML,
    newRootEid,
    newPrimaryEid,
    allNewEids
  };
}

/**
 * Duplicate the repeat root containing `selectedEid`. Clones the existing
 * subtree (including text and child structure), stamps fresh eids across the
 * clone, and returns an eidMap so the caller can clone per-element overrides.
 *
 * @returns {{ updatedHtml, newRootEid, newPrimaryEid, allNewEids, eidMap }}
 *   eidMap maps source eid → new eid for every eid in the duplicated subtree.
 */
export function duplicateNearEid(sectionHtml, selectedEid, opts) {
  const { itemSelector, position = 'after' } = opts || {};
  if (!sectionHtml || !selectedEid || !itemSelector) {
    return { ...NULL_RESULT, eidMap: {} };
  }
  if (position !== 'before' && position !== 'after') {
    return { ...NULL_RESULT, eidMap: {} };
  }

  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return { ...NULL_RESULT, eidMap: {} };

  injectEditableAttrs(root);

  const selected = findByEid(root, selectedEid);
  if (!selected) return { ...NULL_RESULT, eidMap: {} };
  const repeatRoot = selected.closest(itemSelector);
  if (!repeatRoot) return { ...NULL_RESULT, eidMap: {} };

  const clone = repeatRoot.cloneNode(true);

  // Record source eids before rewriting so we can build the map.
  const sourceEids = [];
  if (clone.getAttribute('data-pb-eid')) sourceEids.push(clone.getAttribute('data-pb-eid'));
  for (const el of clone.querySelectorAll('[data-pb-eid]')) {
    sourceEids.push(el.getAttribute('data-pb-eid'));
  }

  // Wipe eids on the clone so the allocator hands out fresh ones (otherwise
  // they would collide with the source we're duplicating from).
  if (clone.hasAttribute('data-pb-eid')) clone.removeAttribute('data-pb-eid');
  for (const el of clone.querySelectorAll('[data-pb-eid]')) {
    el.removeAttribute('data-pb-eid');
  }

  // Make sure data-pb-editable tags are present on the clone (they were
  // already injected on `root`, and cloneNode carries them — but a defensive
  // re-pass that also tags the clone's root keeps this correct even if the
  // source repeat root was never classified).
  classifyIncludingRoot(clone);
  sanitizeSubtreeAttrs(clone);
  allocateEidsForSubtree(root, clone, opts?.editableIdRepairs);
  remapIdentityRefsForSubtree(root, clone, { forceFresh: true });

  // Build the eid map by walking source and clone in parallel order.
  const eidMap = {};
  const cloneEids = [];
  if (clone.getAttribute('data-pb-eid')) cloneEids.push(clone.getAttribute('data-pb-eid'));
  for (const el of clone.querySelectorAll('[data-pb-eid]')) {
    cloneEids.push(el.getAttribute('data-pb-eid'));
  }
  for (let i = 0; i < sourceEids.length && i < cloneEids.length; i++) {
    eidMap[sourceEids[i]] = cloneEids[i];
  }

  const newRootEid = clone.getAttribute('data-pb-eid') || null;
  const newPrimaryEid = pickPrimaryEid(clone, newRootEid);
  const allNewEids = eidsInSubtree(clone);

  if (position === 'before') {
    repeatRoot.parentNode.insertBefore(clone, repeatRoot);
  } else {
    repeatRoot.parentNode.insertBefore(clone, repeatRoot.nextSibling);
  }

  stripTempAttrs(root);
  return {
    updatedHtml: root.innerHTML,
    newRootEid,
    newPrimaryEid,
    allNewEids,
    eidMap
  };
}

/**
 * Physically relocate the repeat root containing `sourceEid` before/after
 * the repeat root containing `targetEid`, preserving every existing eid in
 * the moved subtree. The source/target eids may live on the repeat root or
 * any descendant; both are climbed via `closest(itemSelector)`.
 *
 * @returns {{ updatedHtml: string|null, sourceRootEid: string|null, movedEids: string[] }}
 */
export function moveSiblingPreservingEids(sectionHtml, sourceEid, targetEid, opts) {
  const { itemSelector, position } = opts || {};
  if (!sectionHtml || !sourceEid || !targetEid || !itemSelector) return NULL_MOVE_RESULT;
  if (position !== 'before' && position !== 'after') return NULL_MOVE_RESULT;
  if (sourceEid === targetEid) return NULL_MOVE_RESULT;

  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return NULL_MOVE_RESULT;

  const source = findByEid(root, sourceEid);
  const target = findByEid(root, targetEid);
  if (!source || !target) return NULL_MOVE_RESULT;

  const sourceRoot = source.closest(itemSelector);
  const targetRoot = target.closest(itemSelector);
  if (!sourceRoot || !targetRoot) return NULL_MOVE_RESULT;
  if (sourceRoot === targetRoot) return NULL_MOVE_RESULT;
  if (sourceRoot.contains(targetRoot) || targetRoot.contains(sourceRoot)) return NULL_MOVE_RESULT;

  const parent = sourceRoot.parentNode;
  if (!parent || parent !== targetRoot.parentNode) return NULL_MOVE_RESULT;

  const movedEids = eidsInSubtree(sourceRoot);
  const sourceRootEid = sourceRoot.getAttribute('data-pb-eid') || null;

  sourceRoot.remove();
  const referenceNode = position === 'before' ? targetRoot : targetRoot.nextSibling;
  parent.insertBefore(sourceRoot, referenceNode);

  return {
    updatedHtml: root.innerHTML,
    sourceRootEid,
    movedEids
  };
}

/**
 * Move a repeat root one slot toward its previous ('prev') or next ('next')
 * filtered sibling, preserving eids. Powers keyboard reorder (arrow keys /
 * context-menu Move), which has no drag target — it moves relative to the
 * current position. Returns NULL_MOVE_RESULT at an edge (no adjacent sibling).
 *
 * @param {string} sectionHtml
 * @param {string} sourceEid — the selected element's eid (may be a child of the root)
 * @param {{ itemSelector: string, siblingSelector?: string, direction: 'prev'|'next' }} opts
 * @returns {{ updatedHtml: string|null, sourceRootEid: string|null, movedEids: string[] }}
 */
export function moveAdjacentSiblingPreservingEids(sectionHtml, sourceEid, opts) {
  const { itemSelector, siblingSelector, direction } = opts || {};
  if (!sectionHtml || !sourceEid || !itemSelector) return NULL_MOVE_RESULT;
  if (direction !== 'prev' && direction !== 'next') return NULL_MOVE_RESULT;

  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return NULL_MOVE_RESULT;

  const source = findByEid(root, sourceEid);
  if (!source) return NULL_MOVE_RESULT;
  const sourceRoot = source.closest(itemSelector);
  if (!sourceRoot || !sourceRoot.parentNode) return NULL_MOVE_RESULT;

  const parent = sourceRoot.parentNode;
  const sibSelector = siblingSelector || itemSelector;
  const siblings = Array.from(parent.children).filter((child) => {
    try {
      return child.matches(sibSelector);
    } catch {
      return false;
    }
  });
  const idx = siblings.indexOf(sourceRoot);
  if (idx === -1) return NULL_MOVE_RESULT;
  const targetRoot = direction === 'prev' ? siblings[idx - 1] : siblings[idx + 1];
  if (!targetRoot) return NULL_MOVE_RESULT; // at an edge — nothing to swap with

  const movedEids = eidsInSubtree(sourceRoot);
  const sourceRootEid = sourceRoot.getAttribute('data-pb-eid') || null;

  sourceRoot.remove();
  // 'prev' → insert before the previous sibling; 'next' → insert after the next.
  const referenceNode = direction === 'prev' ? targetRoot : targetRoot.nextSibling;
  parent.insertBefore(sourceRoot, referenceNode);

  return { updatedHtml: root.innerHTML, sourceRootEid, movedEids };
}

/**
 * Move a direct child of a detected header reorder zone before/after another
 * child in the same zone. Markers are runtime-only and stripped before return.
 *
 * @returns {{ updatedHtml: string|null, sourceRootEid: string|null, movedEids: string[] }}
 */
export function moveHeaderSiblingByEid(sectionHtml, sourceEid, targetEid, opts) {
  const { position } = opts || {};
  if (!sectionHtml || !sourceEid || !targetEid) return NULL_MOVE_RESULT;
  if (position !== 'before' && position !== 'after') return NULL_MOVE_RESULT;
  if (sourceEid === targetEid) return NULL_MOVE_RESULT;

  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return NULL_MOVE_RESULT;

  try {
    injectHeaderZoneMarkers(root);

    const source = findByEid(root, sourceEid);
    const target = findByEid(root, targetEid);
    if (!source || !target) return NULL_MOVE_RESULT;

    const sourceUnit = source.closest('[data-pb-reorder-zone] > *');
    const targetUnit = target.closest('[data-pb-reorder-zone] > *');
    if (!sourceUnit || !targetUnit) return NULL_MOVE_RESULT;
    if (sourceUnit === targetUnit) return NULL_MOVE_RESULT;
    if (sourceUnit.contains(targetUnit) || targetUnit.contains(sourceUnit)) return NULL_MOVE_RESULT;

    const parent = sourceUnit.parentNode;
    if (!parent || parent !== targetUnit.parentNode) return NULL_MOVE_RESULT;

    const movedEids = eidsInSubtree(sourceUnit);
    const sourceRootEid = sourceUnit.getAttribute('data-pb-eid') || null;

    sourceUnit.remove();
    const referenceNode = position === 'before' ? targetUnit : targetUnit.nextSibling;
    parent.insertBefore(sourceUnit, referenceNode);

    stripHeaderZoneMarkers(root);
    return {
      updatedHtml: root.innerHTML,
      sourceRootEid,
      movedEids
    };
  } finally {
    stripHeaderZoneMarkers(root);
  }
}

/**
 * Append a child element inside the element identified by `parentEid`.
 * Used when the repeat container itself owns an eid (e.g. an empty
 * <ul class="cs-card-group" data-pb-eid="..."> that the user wants to
 * populate). Does NOT climb via itemSelector — the eid IS the parent.
 */
export function appendChildByEid(sectionHtml, parentEid, childHtml, opts = {}) {
  if (!sectionHtml || !parentEid || !childHtml) return NULL_RESULT;

  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return NULL_RESULT;

  injectEditableAttrs(root);

  const parent = findByEid(root, parentEid);
  if (!parent) return NULL_RESULT;

  const parsed = parseSubtree(childHtml);
  if (!parsed) return NULL_RESULT;
  const { subtree } = parsed;

  classifyIncludingRoot(subtree);
  sanitizeSubtreeAttrs(subtree);
  allocateEidsForSubtree(root, subtree, opts?.editableIdRepairs);

  const newRootEid = subtree.getAttribute('data-pb-eid') || null;
  const newPrimaryEid = pickPrimaryEid(subtree, newRootEid);
  const allNewEids = eidsInSubtree(subtree);

  parent.appendChild(root.ownerDocument.importNode(subtree, true));

  stripTempAttrs(root);
  return {
    updatedHtml: root.innerHTML,
    newRootEid,
    newPrimaryEid,
    allNewEids
  };
}

/**
 * The identity eid of a repeat root: its own eid when it has one, else its
 * first descendant eid in document order.
 *
 * Read-only by design. Eids are stamped on `[data-pb-editable]` elements only
 * (see stampEditableIds), so plenty of real repeat roots — an `li.cs-item`
 * wrapping editable children, a `[data-pb-reorder-zone] > div` — carry none.
 * Minting one here would mean a selection gesture silently rewriting section
 * HTML; the descendant fallback is stable for exactly as long as the unit
 * exists, and every mutation primitive climbs `closest(itemSelector)` from any
 * eid inside the unit anyway.
 */
function unitEidOf(el) {
  if (!el || !el.getAttribute) return null;
  const own = el.getAttribute('data-pb-eid');
  if (own) return own;
  const descendant = el.querySelector ? el.querySelector('[data-pb-eid]') : null;
  return descendant ? descendant.getAttribute('data-pb-eid') : null;
}

/**
 * Enumerate the repeat-root siblings of the unit containing `selectedEid` —
 * the group a sibling multi-selection (Polish 16C1) may span.
 *
 * Constraint C7: NEVER `':scope > ' + itemSelector`. Real adapters register
 * `.cs-button-group > a` and `[data-pb-reorder-zone] > *`, for which that
 * concatenation is a *valid* selector that silently matches grandchildren.
 * Siblings are the parent's own children filtered by `matches(itemSelector)`.
 *
 * @param {string} sectionHtml
 * @param {string} selectedEid — any eid inside the unit (root or descendant)
 * @param {{itemSelector: string}} opts
 * @returns {{unitEid: string, index: number, parentEid: string|null,
 *   units: string[]}|null} `units` is the group in document order.
 */
export function siblingUnitsNearEid(sectionHtml, selectedEid, opts) {
  const { itemSelector } = opts || {};
  if (!sectionHtml || !selectedEid || !itemSelector) return null;

  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return null;

  injectEditableAttrs(root);
  const hasRuntimeMarkers = prepareRuntimeSelectorMarkers(root, itemSelector);
  try {
    const selected = findByEid(root, selectedEid);
    if (!selected) return null;
    const repeatRoot = selected.closest(itemSelector);
    if (!repeatRoot || !repeatRoot.parentElement) return null;

    const units = [];
    let index = -1;
    for (const sibling of Array.from(repeatRoot.parentElement.children)) {
      if (!sibling.matches || !sibling.matches(itemSelector)) continue;
      const unitEid = unitEidOf(sibling);
      if (!unitEid) continue;
      if (sibling === repeatRoot) index = units.length;
      units.push(unitEid);
    }
    if (index === -1) return null;

    return {
      unitEid: units[index],
      index,
      parentEid: repeatRoot.parentElement.getAttribute('data-pb-eid') || null,
      units
    };
  } finally {
    if (hasRuntimeMarkers) stripHeaderZoneMarkers(root);
  }
}

/**
 * Remove the repeat root containing `selectedEid`. Returns the new HTML and
 * the list of eids that were removed (so the caller can prune
 * section.overrides.elements via pruneOverrides()).
 */
export function removeNearEid(sectionHtml, selectedEid, opts) {
  const { itemSelector } = opts || {};
  if (!sectionHtml || !selectedEid || !itemSelector) {
    return { updatedHtml: null, removedEids: [] };
  }

  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return { updatedHtml: null, removedEids: [] };

  injectEditableAttrs(root);
  const hasRuntimeMarkers = prepareRuntimeSelectorMarkers(root, itemSelector);

  const selected = findByEid(root, selectedEid);
  if (!selected) {
    if (hasRuntimeMarkers) stripHeaderZoneMarkers(root);
    return { updatedHtml: null, removedEids: [] };
  }
  const repeatRoot = selected.closest(itemSelector);
  if (!repeatRoot) {
    if (hasRuntimeMarkers) stripHeaderZoneMarkers(root);
    return { updatedHtml: null, removedEids: [] };
  }

  const removedEids = eidsInSubtree(repeatRoot);
  repeatRoot.remove();

  if (hasRuntimeMarkers) stripHeaderZoneMarkers(root);
  stripTempAttrs(root);
  return {
    updatedHtml: root.innerHTML,
    removedEids
  };
}

/**
 * Serialize the repeat-root subtree containing `selectedEid` into a portable
 * HTML fragment + source-eid list. Same-section operations (duplicate) keep
 * using duplicateNearEid; the element clipboard uses this so a snapshot can
 * survive into a cross-section paste.
 *
 * Returns null when the section / eid / itemSelector don't resolve.
 */
export function serializeSubtreeAroundEid(sectionHtml, selectedEid, opts) {
  const { itemSelector } = opts || {};
  if (!sectionHtml || !selectedEid || !itemSelector) return null;

  const { root } = parseHTMLFragment(sectionHtml);
  if (!root) return null;

  injectEditableAttrs(root);
  const hasRuntimeMarkers = prepareRuntimeSelectorMarkers(root, itemSelector);
  const selected = findByEid(root, selectedEid);
  if (!selected) {
    if (hasRuntimeMarkers) stripHeaderZoneMarkers(root);
    stripTempAttrs(root);
    return null;
  }
  const repeatRoot = selected.closest(itemSelector);
  if (!repeatRoot) {
    if (hasRuntimeMarkers) stripHeaderZoneMarkers(root);
    stripTempAttrs(root);
    return null;
  }

  const clone = repeatRoot.cloneNode(true);
  if (hasRuntimeMarkers) {
    stripHeaderZoneMarkers(root);
    stripHeaderZoneMarkers(clone);
  }
  stripTempAttrs(root); // restore original section HTML state
  // The clone carries injected data-pb-editable attrs (from the parent walk
  // that ran on root). Keep them on the clone so the receiver can classify
  // without re-walking. Eids remain as the SOURCE eids — the insert step
  // strips them and allocates fresh ones against the target's pool.

  const sourcePrimaryEid = pickPrimaryEid(clone, clone.getAttribute('data-pb-eid'));
  const sourceEids = eidsInSubtree(clone);

  // Wrap to round-trip through innerHTML so the caller gets a string.
  const wrapper = clone.ownerDocument.createElement('div');
  wrapper.appendChild(clone);
  return {
    htmlFragment: wrapper.innerHTML,
    sourceEids,
    sourcePrimaryEid
  };
}

/**
 * Insert a serialized subtree (from serializeSubtreeAroundEid) into a target
 * section near `targetEid` (or at the end of the section root when omitted).
 * Strips eids on the cloned subtree, allocates fresh ones against the target
 * section's existing eid pool, and returns an eidMap so the caller can clone
 * overrides via applyOverridesCloneToSection.
 *
 * Same shape as duplicateNearEid's return for caller compatibility.
 */
export function insertSerializedSubtreeNearEid(targetSectionHtml, htmlFragment, sourceEids, opts) {
  const {
    nearEid = null,
    position = 'after',
    itemSelector,
    existingIds = [],
    forceFreshIds = true
  } = opts || {};
  if (typeof targetSectionHtml !== 'string' || !htmlFragment) {
    return { ...NULL_RESULT, eidMap: {} };
  }
  if (position !== 'before' && position !== 'after' && position !== 'inside') {
    return { ...NULL_RESULT, eidMap: {} };
  }

  const { root } = parseHTMLFragment(targetSectionHtml);
  if (!root) return { ...NULL_RESULT, eidMap: {} };

  injectEditableAttrs(root);
  const hasRuntimeMarkers = prepareRuntimeSelectorMarkers(root, itemSelector);

  const parsed = parseSubtree(htmlFragment);
  if (!parsed?.subtree) {
    if (hasRuntimeMarkers) stripHeaderZoneMarkers(root);
    stripTempAttrs(root);
    return { ...NULL_RESULT, eidMap: {} };
  }
  const subtree = parsed.subtree;

  // Strip eids on the clone so the allocator hands out fresh ones that don't
  // collide with the target section's existing eids.
  if (subtree.hasAttribute('data-pb-eid')) subtree.removeAttribute('data-pb-eid');
  for (const el of subtree.querySelectorAll('[data-pb-eid]')) {
    el.removeAttribute('data-pb-eid');
  }

  classifyIncludingRoot(subtree);
  sanitizeSubtreeAttrs(subtree);
  allocateEidsForSubtree(root, subtree, opts?.editableIdRepairs);
  remapIdentityRefsForSubtree(root, subtree, {
    forceFresh: forceFreshIds,
    existingIds
  });

  // Build the eid map by walking source and clone in parallel order.
  const eidMap = {};
  const cloneEids = [];
  if (subtree.getAttribute('data-pb-eid')) cloneEids.push(subtree.getAttribute('data-pb-eid'));
  for (const el of subtree.querySelectorAll('[data-pb-eid]')) {
    cloneEids.push(el.getAttribute('data-pb-eid'));
  }
  const sources = Array.isArray(sourceEids) ? sourceEids : [];
  for (let i = 0; i < sources.length && i < cloneEids.length; i++) {
    eidMap[sources[i]] = cloneEids[i];
  }

  const newRootEid = subtree.getAttribute('data-pb-eid') || null;
  const newPrimaryEid = pickPrimaryEid(subtree, newRootEid);
  const allNewEids = eidsInSubtree(subtree);

  // Determine insertion site. 'inside' drops the subtree AS A CHILD of the
  // element identified by nearEid (a container) — the one gap that lets a drag
  // drop into a container rather than only before/after a sibling. 'before' /
  // 'after' place it as a sibling of the nearEid's repeat root.
  let inserted = false;
  if (position === 'inside' && nearEid) {
    const container = findByEid(root, nearEid);
    if (container) {
      container.appendChild(subtree);
      inserted = true;
    }
  } else if (nearEid && itemSelector) {
    const nearTarget = findByEid(root, nearEid);
    if (nearTarget) {
      const targetRepeatRoot = nearTarget.closest(itemSelector);
      if (targetRepeatRoot && targetRepeatRoot.parentNode) {
        if (position === 'before') {
          targetRepeatRoot.parentNode.insertBefore(subtree, targetRepeatRoot);
        } else {
          targetRepeatRoot.parentNode.insertBefore(subtree, targetRepeatRoot.nextSibling);
        }
        inserted = true;
      }
    }
  }
  if (!inserted) {
    // Fail closed: with no resolvable insertion site we must NOT append the
    // subtree to the parsing root — that lands it as a sibling of the real
    // <section>, outside CSS/export scope and stored HTML integrity. Refuse and
    // let the caller surface "could not paste here" / revert the move.
    if (hasRuntimeMarkers) stripHeaderZoneMarkers(root);
    stripTempAttrs(root);
    return { ...NULL_RESULT, eidMap: {} };
  }

  if (hasRuntimeMarkers) stripHeaderZoneMarkers(root);
  stripTempAttrs(root);
  return {
    updatedHtml: root.innerHTML,
    newRootEid,
    newPrimaryEid,
    allNewEids,
    eidMap
  };
}

/**
 * Merge a snapshot of source-side per-element overrides into the target
 * section's overrides object under fresh eids (per `eidMap`). Source entries
 * may not exist on the target section (cross-section paste); we add them.
 *
 * Returns a NEW overrides object (immutable-style) for `store.update`, or null
 * if no entries needed migration.
 */
export function applyOverridesCloneToSection(targetSection, sourceOverridesByEid, eidMap) {
  if (!sourceOverridesByEid || typeof sourceOverridesByEid !== 'object') return null;
  if (!eidMap || typeof eidMap !== 'object') return null;

  const baseOverrides = targetSection?.overrides || {};
  const baseElements = baseOverrides.elements || {};
  const nextElements = { ...baseElements };
  let changed = false;

  for (const [sourceEid, newEid] of Object.entries(eidMap)) {
    const src = sourceOverridesByEid[sourceEid];
    if (!src) continue;
    nextElements[newEid] = JSON.parse(JSON.stringify(src));
    changed = true;
  }

  if (!changed) return null;
  return { ...baseOverrides, elements: nextElements };
}

/**
 * Clone per-element overrides entries via an eidMap (typically returned by
 * duplicateNearEid). Each entry under a source eid is deep-cloned and stored
 * under the new eid. Source entries are preserved so both copies keep their
 * styling.
 *
 * Returns a NEW overrides object (immutable-style) for `store.update`, or null
 * if no cloneable entries existed.
 */
export function cloneOverridesViaEidMap(section, eidMap) {
  if (!section?.overrides?.elements || !eidMap) return null;
  const sourceElements = section.overrides.elements;
  const next = { ...sourceElements };
  let changed = false;
  for (const [sourceEid, newEid] of Object.entries(eidMap)) {
    const src = sourceElements[sourceEid];
    if (!src) continue;
    next[newEid] = JSON.parse(JSON.stringify(src));
    changed = true;
  }
  if (!changed) return null;
  return { ...section.overrides, elements: next };
}

/**
 * Drop section.overrides.elements entries whose eids no longer exist after
 * a remove. Returns a new section object (immutable-style) — caller persists
 * via store.update on `${sectionPath}.overrides`.
 */
export function pruneOverrides(section, removedEids) {
  if (!section || !section.overrides || !section.overrides.elements) return section;
  if (!Array.isArray(removedEids) || removedEids.length === 0) return section;

  const removed = new Set(removedEids);
  const nextElements = {};
  let changed = false;
  for (const [eid, value] of Object.entries(section.overrides.elements)) {
    if (removed.has(eid)) {
      changed = true;
      continue;
    }
    nextElements[eid] = value;
  }
  if (!changed) return section;

  return {
    ...section,
    overrides: {
      ...section.overrides,
      elements: nextElements
    }
  };
}
